import { c as _c } from "react/compiler-runtime";
import { Dialog } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import { css, cva, cx } from '@repo/panda/css';
import type { SystemStyleObject } from '@repo/panda/types';
import { type ReactNode, useRef } from 'react';
import { IconButton } from '../ds/button';
import { Icon } from '../ds/icon';
import { isWithinToast } from '../ds/toast/toast';
import { Type } from './type';
export interface ModalProps {
  isOpen: boolean;
  'aria-label'?: string;
  onOpenChange?: (details: Dialog.OpenChangeDetails) => void;
  onCloseButtonClick?: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  hideCloseButton?: boolean;
  hideBackdrop?: boolean;
  className?: string;
  backdropClassName?: SystemStyleObject;
  positionerClassName?: SystemStyleObject;
  closeButtonClassName?: SystemStyleObject;
  size?: ModalVariants['size'];
  preventScroll?: boolean;
  closeOnEscape?: boolean;
  closeOnInteractOutside?: boolean;
  variant?: ModalVariants['variant'];
  disableInternalScrolling?: boolean;
  /** Delay rendering modal content until first opened. */
  lazyMount?: boolean;
  /** Unmount modal content when closed. */
  unmountOnExit?: boolean;
  zIndex?: number;
}
const modalStyles = cva({
  base: {
    color: 'text.primary',
    background: 'black',
    borderRadius: '24',
    boxShadow: 'lg',
    padding: '16',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    gap: '12',
    '&[data-state="open"]': {
      animation: '[modal-open 0.15s ease-out forwards]'
    },
    '&[data-state="closed"]': {
      animation: '[modal-close 0.15s ease-in forwards]'
    }
  },
  variants: {
    variant: {
      transparent: {
        background: 'transparent'
      },
      darkBlur: {
        background: 'blackAlpha.60',
        backdropFilter: '[blur(12px)]'
      }
    },
    size: {
      sm: {
        maxWidth: {
          base: 'full',
          sm: '[400px]'
        },
        minWidth: {
          base: 'full',
          sm: '[400px]'
        },
        width: {
          base: 'full',
          sm: '[400px]'
        },
        maxHeight: 'full'
      },
      md: {
        maxWidth: {
          base: 'full',
          md: '[600px]'
        },
        minWidth: {
          base: 'full',
          md: '[600px]'
        },
        width: {
          base: 'full',
          md: '[600px]'
        },
        maxHeight: 'full'
      },
      lg: {
        maxWidth: {
          base: 'full',
          lg: '[800px]'
        },
        minWidth: {
          base: 'full',
          lg: '[800px]'
        },
        width: {
          base: 'full',
          lg: '[800px]'
        },
        maxHeight: 'full'
      },
      xl: {
        maxWidth: {
          base: 'full',
          xl: '[1000px]'
        },
        minWidth: {
          base: 'full',
          xl: '[1000px]'
        },
        width: {
          base: 'full',
          xl: '[1000px]'
        },
        maxHeight: 'full'
      },
      full: {
        maxWidth: 'screen',
        minWidth: 'screen',
        width: 'screen',
        height: 'screen',
        '&[data-state="open"]': {
          animation: '[fade-in 0.15s ease-out forwards]'
        },
        '&[data-state="closed"]': {
          animation: '[fade-out 0.15s ease-in forwards]'
        }
      },
      // Use when the consumer wants to fully control the modal's dimensions via `className`.
      // No size-related styles will be applied in this case.
      custom: {
        maxHeight: 'full'
      }
    }
  },
  defaultVariants: {
    size: 'md'
  }
});
export type ModalVariants = NonNullable<Parameters<typeof modalStyles>[0]>;

// Toasts are layered above modals, so using one isn't an interaction outside the modal.
const ignoreToastInteractions = (event: {
  detail: {
    originalEvent: Event;
  };
  preventDefault: () => void;
}) => {
  if (isWithinToast(event.detail.originalEvent.target)) {
    event.preventDefault();
  }
};
export const Modal = t0 => {
  const $ = _c(92);
  const {
    isOpen,
    "aria-label": ariaLabel,
    onOpenChange,
    onCloseButtonClick,
    title,
    description,
    children,
    hideCloseButton,
    hideBackdrop,
    variant,
    className,
    backdropClassName,
    positionerClassName,
    closeButtonClassName,
    size: t1,
    preventScroll: t2,
    closeOnEscape: t3,
    closeOnInteractOutside: t4,
    disableInternalScrolling: t5,
    zIndex: t6,
    lazyMount,
    unmountOnExit: t7
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const preventScroll = t2 === undefined ? true : t2;
  const closeOnEscape = t3 === undefined ? true : t3;
  const closeOnInteractOutside = t4 === undefined ? true : t4;
  const disableInternalScrolling = t5 === undefined ? false : t5;
  const zIndex = t6 === undefined ? 50 : t6;
  const unmountOnExit = t7 === undefined ? true : t7;
  const contentRef = useRef(null);
  let T0;
  let T1;
  let T2;
  let T3;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t8;
  let t9;
  if ($[0] !== ariaLabel || $[1] !== backdropClassName || $[2] !== children || $[3] !== className || $[4] !== closeButtonClassName || $[5] !== closeOnEscape || $[6] !== closeOnInteractOutside || $[7] !== description || $[8] !== disableInternalScrolling || $[9] !== hideBackdrop || $[10] !== hideCloseButton || $[11] !== isOpen || $[12] !== lazyMount || $[13] !== onCloseButtonClick || $[14] !== onOpenChange || $[15] !== positionerClassName || $[16] !== preventScroll || $[17] !== size || $[18] !== title || $[19] !== unmountOnExit || $[20] !== variant || $[21] !== zIndex) {
    const backdropStyle = css({
      background: "blackAlpha.50",
      position: "fixed",
      inset: "0",
      zIndex: "[50]",
      "&[data-state=\"open\"]": {
        animation: "[fade-in 0.2s ease-out forwards]"
      },
      "&[data-state=\"closed\"]": {
        animation: "[fade-out 0.2s ease-in forwards]"
      }
    });
    const positionerStyle = css({
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
      position: "fixed",
      inset: "0",
      zIndex: "[50]",
      width: "[100vw]",
      height: "[100vh]",
      overflow: "auto",
      padding: size === "full" ? "0" : {
        base: "8",
        sm: "12",
        md: "16"
      }
    });
    let t26;
    if ($[44] !== className || $[45] !== size || $[46] !== variant) {
      t26 = cx(modalStyles({
        size,
        variant
      }), className);
      $[44] = className;
      $[45] = size;
      $[46] = variant;
      $[47] = t26;
    } else {
      t26 = $[47];
    }
    const contentStyle = t26;
    const closeTriggerStyle = css.raw({
      position: "absolute",
      top: "16",
      right: "16",
      zIndex: 10
    });
    const closeButtonStyle = Object.assign({}, closeTriggerStyle, variant === "darkBlur" ? {
      backgroundColor: "whiteAlpha.15",
      backdropFilter: "[blur(12px)]"
    } : {}, closeButtonClassName);
    let t27;
    if ($[48] !== disableInternalScrolling) {
      t27 = css({
        flexGrow: 1,
        ...(disableInternalScrolling ? {} : {
          overflowY: "auto"
        })
      });
      $[48] = disableInternalScrolling;
      $[49] = t27;
    } else {
      t27 = $[49];
    }
    const childrenContainerStyle = t27;
    const t28 = hideCloseButton ? "0" : "[44px]";
    let t29;
    if ($[50] !== t28) {
      t29 = css({
        display: "flex",
        flexDirection: "column",
        gap: "8",
        paddingRight: t28,
        minWidth: "0"
      });
      $[50] = t28;
      $[51] = t29;
    } else {
      t29 = $[51];
    }
    const headerContainerStyle = t29;
    let t30;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = css({
        wordWrap: "break-word",
        overflowWrap: "break-word",
        hyphens: "auto",
        lineHeight: "tight",
        color: "white"
      });
      $[52] = t30;
    } else {
      t30 = $[52];
    }
    const titleStyle = t30;
    let t31;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = css({
        wordWrap: "break-word",
        overflowWrap: "break-word",
        hyphens: "auto"
      });
      $[53] = t31;
    } else {
      t31 = $[53];
    }
    const descriptionStyle = t31;
    T3 = Dialog.Root;
    t15 = isOpen;
    t16 = onOpenChange;
    t17 = lazyMount;
    t18 = unmountOnExit;
    t19 = preventScroll;
    t20 = closeOnEscape;
    t21 = closeOnInteractOutside;
    t22 = ignoreToastInteractions;
    t23 = true;
    t24 = true;
    t25 = ariaLabel;
    T2 = Portal;
    t14 = !hideBackdrop && <Dialog.Backdrop className={cx(backdropStyle, css(backdropClassName))} style={{
      zIndex
    }} />;
    T1 = Dialog.Positioner;
    t12 = cx(positionerStyle, css(positionerClassName));
    if ($[54] !== zIndex) {
      t13 = {
        zIndex
      };
      $[54] = zIndex;
      $[55] = t13;
    } else {
      t13 = $[55];
    }
    T0 = Dialog.Content;
    t8 = contentStyle;
    if ($[56] !== description || $[57] !== headerContainerStyle || $[58] !== title) {
      t9 = (title || description) && <div className={headerContainerStyle}>{title && <Dialog.Title asChild={true}><Type variant="header-l" className={titleStyle}>{title}</Type></Dialog.Title>}{description && <Dialog.Description asChild={true}><Type variant="body-s" className={descriptionStyle}>{description}</Type></Dialog.Description>}</div>;
      $[56] = description;
      $[57] = headerContainerStyle;
      $[58] = title;
      $[59] = t9;
    } else {
      t9 = $[59];
    }
    if ($[60] !== children || $[61] !== childrenContainerStyle) {
      t10 = children && <div ref={contentRef} className={childrenContainerStyle}>{children}</div>;
      $[60] = children;
      $[61] = childrenContainerStyle;
      $[62] = t10;
    } else {
      t10 = $[62];
    }
    t11 = !hideCloseButton && <Dialog.CloseTrigger asChild={true}><IconButton onClick={onCloseButtonClick} color="secondary" size="sm" aria-label="Close dialog" className={css(closeButtonStyle)}><Icon icon="close" decorative={true} /></IconButton></Dialog.CloseTrigger>;
    $[0] = ariaLabel;
    $[1] = backdropClassName;
    $[2] = children;
    $[3] = className;
    $[4] = closeButtonClassName;
    $[5] = closeOnEscape;
    $[6] = closeOnInteractOutside;
    $[7] = description;
    $[8] = disableInternalScrolling;
    $[9] = hideBackdrop;
    $[10] = hideCloseButton;
    $[11] = isOpen;
    $[12] = lazyMount;
    $[13] = onCloseButtonClick;
    $[14] = onOpenChange;
    $[15] = positionerClassName;
    $[16] = preventScroll;
    $[17] = size;
    $[18] = title;
    $[19] = unmountOnExit;
    $[20] = variant;
    $[21] = zIndex;
    $[22] = T0;
    $[23] = T1;
    $[24] = T2;
    $[25] = T3;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
    $[39] = t23;
    $[40] = t24;
    $[41] = t25;
    $[42] = t8;
    $[43] = t9;
  } else {
    T0 = $[22];
    T1 = $[23];
    T2 = $[24];
    T3 = $[25];
    t10 = $[26];
    t11 = $[27];
    t12 = $[28];
    t13 = $[29];
    t14 = $[30];
    t15 = $[31];
    t16 = $[32];
    t17 = $[33];
    t18 = $[34];
    t19 = $[35];
    t20 = $[36];
    t21 = $[37];
    t22 = $[38];
    t23 = $[39];
    t24 = $[40];
    t25 = $[41];
    t8 = $[42];
    t9 = $[43];
  }
  let t26;
  if ($[63] !== T0 || $[64] !== t10 || $[65] !== t11 || $[66] !== t8 || $[67] !== t9) {
    t26 = <T0 className={t8}>{t9}{t10}{t11}</T0>;
    $[63] = T0;
    $[64] = t10;
    $[65] = t11;
    $[66] = t8;
    $[67] = t9;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== T1 || $[70] !== t12 || $[71] !== t13 || $[72] !== t26) {
    t27 = <T1 className={t12} style={t13}>{t26}</T1>;
    $[69] = T1;
    $[70] = t12;
    $[71] = t13;
    $[72] = t26;
    $[73] = t27;
  } else {
    t27 = $[73];
  }
  let t28;
  if ($[74] !== T2 || $[75] !== t14 || $[76] !== t27) {
    t28 = <T2>{t14}{t27}</T2>;
    $[74] = T2;
    $[75] = t14;
    $[76] = t27;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== T3 || $[79] !== t15 || $[80] !== t16 || $[81] !== t17 || $[82] !== t18 || $[83] !== t19 || $[84] !== t20 || $[85] !== t21 || $[86] !== t22 || $[87] !== t23 || $[88] !== t24 || $[89] !== t25 || $[90] !== t28) {
    t29 = <T3 open={t15} onOpenChange={t16} lazyMount={t17} unmountOnExit={t18} preventScroll={t19} closeOnEscape={t20} closeOnInteractOutside={t21} onInteractOutside={t22} trapFocus={t23} restoreFocus={t24} aria-label={t25}>{t28}</T3>;
    $[78] = T3;
    $[79] = t15;
    $[80] = t16;
    $[81] = t17;
    $[82] = t18;
    $[83] = t19;
    $[84] = t20;
    $[85] = t21;
    $[86] = t22;
    $[87] = t23;
    $[88] = t24;
    $[89] = t25;
    $[90] = t28;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  return t29;
};